import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { FeedbackResponseState } from '../store/FeedbackResponseState';
import { FeedbackQuestionsState } from '../store/FeedbackQuestionsState';
import { UIState } from '../store/UIState';
import {
  FEEDBACK_FORM_PROPERTIES,
  SUBMIT_BUTTON_PROPERTIES,
} from '../constants/FeedbackConstants';
import FeedbackForm from '../components/FeedbackForm';

/**
 * This will be the entry point of the feedback service for host applications.
 * @param {string} intent - Intent of feedback, can be general or triggered. This is a required prop
 * @param {string} title - represents the title of the feedback form. Default value will be used if not provided as prop
 * @param {string} confirmationMessage - represents the confirmation message of feedback submission. Default value will be used if not provided as prop
 * @param {string} submitButtonSize - represents the size of the submit button on feedback form. Default value will be used if not provided as prop
 * @param {string} submitButtonType - represents the type of the submit button on feedback form. Default value will be used if not provided as prop
 * @param {string} submitButtonText - represents the text of the submit button on feedback form. Default value will be used if not provided as prop
 * @param {string} applicationId - The application Id where the feedback module is integrated
 * @param {string} feedbackSubmissionPageIdentifier - The identifier of the page from which feedback was submitted
 * @param {string} displayMode - flag to control darkMode.
 * @param {{ isTriggered: boolean, resetTrigger: () => any }} triggeredObj - Triggered object containing details about when feedback is triggered from outside and how to handle reset
 *
 * @returns - FeedbackForm containing the questionnaire provided and its type will be according to the intent passed.
 */
const Feedback = ({
  feedbackIntent,
  title,
  description,
  confirmationMessage,
  submitButtonSize,
  submitButtonType,
  submitButtonText,
  applicationId,
  feedbackSubmissionPageIdentifier,
  triggeredObj,
  userId,
  isSkippable,
  prePopulate,
  displayMode
}) => {
  return (
    <FeedbackQuestionsState
      feedbackIntent={feedbackIntent}
      applicationId={applicationId}>
      <FeedbackResponseState>
        <UIState>
          <FeedbackForm
            title={title}
            feedbackIntent={feedbackIntent}
            description={description}
            confirmationMessage={confirmationMessage}
            submitButtonSize={submitButtonSize}
            submitButtonType={submitButtonType}
            submitButtonText={submitButtonText}
            applicationId={applicationId}
            feedbackSubmissionPageIdentifier={feedbackSubmissionPageIdentifier}
            triggeredObj={triggeredObj}
            userId={userId}
            isSkippable={isSkippable}
            prePopulate={prePopulate}
            displayMode={displayMode}
          />
        </UIState>
      </FeedbackResponseState>
    </FeedbackQuestionsState>
  );
};

Feedback.propTypes = {
  feedbackIntent: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  confirmationMessage: PropTypes.string,
  submitButtonSize: PropTypes.string,
  submitButtonType: PropTypes.string,
  submitButtonText: PropTypes.string,
  applicationId: PropTypes.string.isRequired,
  feedbackSubmissionPageIdentifier: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  triggeredObj: PropTypes.shape({
    isTriggered: PropTypes.bool.isRequired,
    resetTrigger: PropTypes.func.isRequired,
  }),
  userId: PropTypes.string,
  isSkippable: PropTypes.bool,
  prePopulate: PropTypes.object,
};

Feedback.defaultProps = {
  title: FEEDBACK_FORM_PROPERTIES.title,
  description: FEEDBACK_FORM_PROPERTIES.description,
  confirmationMessage: FEEDBACK_FORM_PROPERTIES.confirmationMessage,
  submitButtonSize: SUBMIT_BUTTON_PROPERTIES.size,
  submitButtonType: SUBMIT_BUTTON_PROPERTIES.type,
  submitButtonText: SUBMIT_BUTTON_PROPERTIES.text,
  feedbackSubmissionPageIdentifier: null,
  triggeredObj: {
    isTriggered: false,
    resetTrigger: noop,
  },
  userId: null,
  isSkippable: true,
  prePopulate: {},
};

export default Feedback;
